/**
 * Prescription Data Completeness Check
 * 
 * Shows a check for intake data and IntelliHealth analysis before creating prescriptions.
 * Displayed when a patient is selected in the prescription form.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  AlertTriangle, 
  CheckCircle2, 
  ClipboardList, 
  Sparkles, 
  Loader2,
  ExternalLink
} from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { getPatientIntakeStatus } from '@/services/intakeService';

interface PrescriptionDataCheckProps {
  patientId: string;
  patientName?: string;
  onSendIntake?: () => void;
  onRunIntelliHealth?: () => void;
  compact?: boolean;
}

interface DataStatus {
  hasCompletedIntake: boolean;
  hasNewResponses: boolean;
  hasPendingIntake: boolean;
  lastResponseDate?: string;
  intakeCount: number;
  isLoading: boolean;
}

const PrescriptionDataCheck: React.FC<PrescriptionDataCheckProps> = ({
  patientId,
  patientName,
  onSendIntake,
  onRunIntelliHealth,
  compact = false
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/practitioner-portal') ? '/practitioner-portal' : '';
  const [status, setStatus] = useState<DataStatus>({
    hasCompletedIntake: false,
    hasNewResponses: false,
    hasPendingIntake: false,
    intakeCount: 0,
    isLoading: true
  });

  useEffect(() => {
    const checkData = async () => {
      if (!patientId) return;
      
      try {
        const result = await getPatientIntakeStatus(patientId);
        
        if (result.success && result.data) {
          const { pending, completed, reviewed } = result.data;
          const allCompleted = [...completed, ...reviewed];
          
          setStatus({
            hasCompletedIntake: allCompleted.length > 0,
            hasNewResponses: completed.length > 0,
            hasPendingIntake: pending.length > 0,
            intakeCount: allCompleted.length,
            lastResponseDate: allCompleted[0]?.completedAt || allCompleted[0]?.responseCompletedAt,
            isLoading: false
          });
        }
      } catch (error) {
        console.error('Error checking patient data:', error);
        setStatus(prev => ({ ...prev, isLoading: false }));
      }
    };

    checkData();
  }, [patientId]);

  if (status.isLoading) {
    return (
      <div className="flex items-center gap-2 py-2 text-slate-500">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span className="text-sm">Checking patient data...</span>
      </div>
    );
  }

  const isComplete = status.hasCompletedIntake;
  const needsAttention = !status.hasCompletedIntake || status.hasNewResponses;

  if (compact) {
    return (
      <div className="flex items-center gap-3 py-2">
        <div className="flex items-center gap-2">
          {status.hasCompletedIntake ? (
            <Badge className="bg-green-50 text-green-700 border-green-200">
              <CheckCircle2 className="w-3 h-3 mr-1" />
              Intake Complete
            </Badge>
          ) : status.hasPendingIntake ? (
            <Badge className="bg-amber-50 text-amber-700 border-amber-200">
              <ClipboardList className="w-3 h-3 mr-1" />
              Awaiting Response
            </Badge>
          ) : (
            <Badge className="bg-slate-100 text-slate-600 border-slate-200">
              <AlertTriangle className="w-3 h-3 mr-1" />
              No Intake
            </Badge>
          )}
        </div>
        {status.hasNewResponses && (
          <Badge className="bg-blue-50 text-blue-700 border-blue-200">
            New Response
          </Badge>
        )}
      </div>
    );
  }

  if (!needsAttention) {
    return (
      <Alert className="bg-green-50 border-green-200">
        <CheckCircle2 className="h-4 w-4 text-green-600" />
        <AlertTitle className="text-green-800">Patient Data Complete</AlertTitle>
        <AlertDescription className="text-green-700">
          {status.intakeCount} completed intake{status.intakeCount !== 1 ? 's' : ''} on file.
          {status.lastResponseDate && (
            <span className="ml-1">
              Last response: {new Date(status.lastResponseDate).toLocaleDateString()}
            </span>
          )}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Alert className="bg-amber-50 border-amber-200">
      <AlertTriangle className="h-4 w-4 text-amber-600" />
      <AlertTitle className="text-amber-800">Data Completeness Check</AlertTitle>
      <AlertDescription className="text-amber-700">
        <div className="mt-2 space-y-2">
          {!status.hasCompletedIntake && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ClipboardList className="w-4 h-4" />
                <span>
                  {status.hasPendingIntake 
                    ? 'Intake form sent, awaiting patient response'
                    : 'No intake data collected for this patient'}
                </span>
              </div>
              {!status.hasPendingIntake && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs border-amber-300 hover:bg-amber-100"
                  onClick={() => {
                    if (onSendIntake) {
                      onSendIntake();
                    } else {
                      navigate(`${basePath}/patients/${patientId}?tab=intakes`);
                    }
                  }}
                >
                  <ClipboardList className="w-3 h-3 mr-1" />
                  Send Intake
                </Button>
              )}
            </div>
          )}
          
          {status.hasNewResponses && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                <span>New intake responses available - consider running IntelliHealth</span>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs border-blue-300 hover:bg-blue-50 text-blue-700"
                onClick={() => {
                  if (onRunIntelliHealth) {
                    onRunIntelliHealth();
                  } else {
                    navigate('/practitioner-portal/intellihealth', { 
                      state: { selectedPatientId: patientId } 
                    });
                  }
                }}
              >
                <Sparkles className="w-3 h-3 mr-1" />
                IntelliHealth
              </Button>
            </div>
          )}
        </div>
        
        <p className="mt-3 text-xs text-amber-600">
          You can still proceed with the prescription, but complete patient data helps ensure accurate treatment.
        </p>
      </AlertDescription>
    </Alert>
  );
};

export default PrescriptionDataCheck;
